"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardBody, CardHeader, CardTitle, Badge, Input, Row, Col, FormGroup, Label } from "reactstrap"
import DataTable from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import { toast } from "react-toastify"
import VerificationService from "../../../Service/access-verification-service"
import type { ExhibitorVerification } from "./verification"

interface VerificationListProps {
  onAdd: () => void
  onEdit: (verification: ExhibitorVerification) => void
  refresh: boolean
}

export default function VerificationList({ onAdd, onEdit, refresh }: VerificationListProps) {
  const [allVerifications, setAllVerifications] = useState<ExhibitorVerification[]>([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState<number | null>(null)
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    verified: 0,
    expired: 0,
    failed: 0,
    time_expired: 0,
  })

  // Frontend filtering states
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [expiryFilter, setExpiryFilter] = useState("all")

  // ✅ Frontend filtering logic
  const filteredVerifications = useMemo(() => {
    let filtered = allVerifications

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (verification) =>
          verification.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          verification.user.person?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          verification.exhibitor.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((verification) => verification.status === statusFilter)
    }

    // Expiry filter
    if (expiryFilter !== "all") {
      const isExpired = expiryFilter === "true"
      filtered = filtered.filter((verification) => verification.is_expired === isExpired)
    }

    return filtered
  }, [allVerifications, searchTerm, statusFilter, expiryFilter])

  const fetchVerifications = async () => {
    try {
      setLoading(true)
      // ✅ Fetch all data without filters - let frontend handle filtering
      const response = await VerificationService.getAll(1, 1000) // Get large page to get all data
      setAllVerifications(response.results)

      // Fetch statistics
      const stats = await VerificationService.getStatistics()
      setStatistics(stats)
    } catch (error) {
      console.error("Failed to fetch verifications:", error)
      toast.error("Failed to load verification data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVerifications()
  }, [refresh])

  const handleToggleStatus = async (id: number) => {
    try {
      setUpdating(id)
      const updatedVerification = await VerificationService.toggleStatus(id)
      setAllVerifications((prev) => prev.map((v) => (v.id === id ? updatedVerification : v)))
      toast.success("Verification status updated successfully")

      // Refresh statistics
      const stats = await VerificationService.getStatistics()
      setStatistics(stats)
    } catch (error) {
      console.error("Failed to toggle verification status:", error)
      toast.error("Failed to update verification status")
    } finally {
      setUpdating(null)
    }
  }

  const handleMarkExpired = async (id: number) => {
    try {
      setUpdating(id)
      const updatedVerification = await VerificationService.markAsExpired(id)
      setAllVerifications((prev) => prev.map((v) => (v.id === id ? updatedVerification : v)))
      toast.success("Verification marked as expired successfully")

      // Refresh statistics
      const stats = await VerificationService.getStatistics()
      setStatistics(stats)
    } catch (error) {
      console.error("Failed to mark verification as expired:", error)
      toast.error("Failed to mark verification as expired")
    } finally {
      setUpdating(null)
    }
  }

  

  const getStatusBadge = (status: string) => {
    
    switch (status) {
      case "verified":
        return <Badge color="success">Verified</Badge>
      case "expired":
        return <Badge color="warning">Expired</Badge>
      case "pending":
        return <Badge color="info">Pending</Badge>
      case "failed":
        return <Badge color="danger">Failed</Badge>
      default:
        return <Badge color="secondary">{status}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const canToggleStatus = (verification: ExhibitorVerification) => {
    return ["verified", "expired"].includes(verification.status)
  }

  const canMarkExpired = (verification: ExhibitorVerification) => {
    return verification.status === "verified" && !verification.is_expired
  }

  // ✅ DataTable columns configuration
  const columns = [
    {
      name: "User",
      selector: (row: ExhibitorVerification) => row.user.person?.full_name || "N/A",
      sortable: true,
      cell: (row: ExhibitorVerification) => (
        <div>
          <div className="fw-bold">{row.user.person?.full_name || "N/A"}</div>
          <small className="text-muted">{row.user.email}</small>
        </div>
      ),
    },
    {
      name: "Exhibitor",
      selector: (row: ExhibitorVerification) => row.exhibitor.name,
      sortable: true,
      cell: (row: ExhibitorVerification) => (
        <div>
          <div className="fw-bold">{row.exhibitor.name}</div>
          {row.exhibitor.description && (
            <small className="text-muted">
              {row.exhibitor.description.substring(0, 50)}
              {row.exhibitor.description.length > 50 && "..."}
            </small>
          )}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row: ExhibitorVerification) => row.status,
      sortable: true,
      cell: (row: ExhibitorVerification) => (
        <div className="d-flex flex-column gap-1">
          {getStatusBadge(row.status)}
          
        </div>
      ),
    },
    {
      name: "Created",
      selector: (row: ExhibitorVerification) => row.created_at,
      sortable: true,
      cell: (row: ExhibitorVerification) => <small>{formatDate(row.created_at)}</small>,
    },
    {
      name: "Verified",
      selector: (row: ExhibitorVerification) => row.verified_at || "",
      sortable: true,
      cell: (row: ExhibitorVerification) => (
        <small>{row.verified_at ? formatDate(row.verified_at) : "Not verified"}</small>
      ),
    },
    // {
    //   name: "Expires",
    //   selector: (row: ExhibitorVerification) => row.expires_at,
    //   sortable: true,
    //   cell: (row: ExhibitorVerification) => (
    //     <small className={row.is_expired ? "text-danger" : ""}>{formatDate(row.expires_at)}</small>
    //   ),
    // },
    {
      name: "Attempts",
      selector: (row: ExhibitorVerification) => row.attempts,
      sortable: true,
      cell: (row: ExhibitorVerification) => (
        <Badge color={row.attempts >= row.max_attempts ? "danger" : "info"}>
          {row.attempts}/{row.max_attempts}
        </Badge>
      ),
    },
    {
      name: "Actions",
      cell: (row: ExhibitorVerification) => (
        <div className="d-flex gap-2">
          {canToggleStatus(row) && (
            <Btn
              color={row.status === "verified" ? "warning" : "success"}
              size="sm"
              onClick={() => handleToggleStatus(row.id)}
              disabled={updating === row.id}
            >
              {updating === row.id ? (
                <i className="fa fa-spinner fa-spin"></i>
              ) : (
                <i className={`fa ${row.status === "verified" ? "fa-times-circle" : "fa-check-circle"}`}></i>
              )}
            </Btn>
          )}
          {canMarkExpired(row) && (
            <Btn color="danger" size="sm" onClick={() => handleMarkExpired(row.id)} disabled={updating === row.id}>
              {updating === row.id ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-clock"></i>}
            </Btn>
          )}
        </div>
      ),
    },
  ]

  return (
    <Card className="shadow">
      <CardHeader className="bg-primary text-white">
        <CardTitle tag="h4" className="mb-0">
          <i className="fa fa-shield-alt me-2"></i>
          Verification Access Management
        </CardTitle>
        <small className="text-light">Manage exhibitor verification statuses and access controls</small>
      </CardHeader>
      <CardBody>
        {/* Filters and Search */}
        <Row className="mb-4">
          <Col md="8">
            <FormGroup>
              <Label for="search" className="fw-bold">
                <i className="fa fa-search me-1"></i>
                Search
              </Label>
              <Input
                type="text"
                id="search"
                placeholder="Search by email, name, or exhibitor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control-lg"
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="statusFilter" className="fw-bold">
                Status Filter
              </Label>
              <Input
                type="select"
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-control-lg"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="expired">Expired</option>
                <option value="failed">Failed</option>
              </Input>
            </FormGroup>
          </Col>
          
          
        </Row>

        {/* Statistics */}
        <Row className="mb-4">
          <Col md="3">
            <Card className="bg-info text-white">
              <CardBody className="text-center">
                <h3 className="mb-0">{statistics.total}</h3>
                <small>Total Verifications</small>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="bg-success text-white">
              <CardBody className="text-center">
                <h3 className="mb-0">{statistics.verified}</h3>
                <small>Verified</small>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="bg-warning text-white">
              <CardBody className="text-center">
                <h3 className="mb-0">{statistics.expired}</h3>
                <small>Expired</small>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="bg-secondary text-white">
              <CardBody className="text-center">
                <h3 className="mb-0">{statistics.pending}</h3>
                <small>Pending</small>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ✅ DataTable */}
        <DataTable
          columns={columns}
          data={filteredVerifications}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          progressPending={loading}
          progressComponent={
            <div className="text-center py-5">
              <i className="fa fa-spinner fa-spin fa-2x"></i>
              <p className="mt-2">Loading verification data...</p>
            </div>
          }
          noDataComponent={
            <div className="text-center py-5">
              <i className="fa fa-info-circle me-2"></i>
              No verification records found matching your criteria.
            </div>
          }
          highlightOnHover
          striped
          responsive
        />
      </CardBody>
    </Card>
  )
}
